import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Field, Heading, Input, Text, Textarea } from "@/components/ui";
import { JoinPolicySetting } from "@/components/admin/join-policy-setting";
import { apiErrorText } from "@/lib/api-error";
import {
  addApprovedEmails,
  fetchApprovedEmails,
  fetchJoinSettings,
  removeApprovedEmail,
  type AddApprovedEmailsResult,
  type ApprovedEmail,
  type JoinSettings,
  type PageMeta,
} from "@/services/api/allowlist";

const PAGE_SIZE = 50;

/**
 * §31 Admin → Approved emails: the "who can join" setting, adding by paste or CSV, search, and
 * removal (which, in an approved-only school, signs that student out at once).
 */
export function ApprovedEmailsAdmin() {
  const t = useTranslations("allowlist");
  const tRoot = useTranslations();
  const locale = useLocale();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, fallbackKey, { namespace: "allowlist.errors" });

  const [settings, setSettings] = useState<JoinSettings | null>(null);
  const [entries, setEntries] = useState<ApprovedEmail[]>([]);
  const [meta, setMeta] = useState<PageMeta>({ total: 0, page: 1, limit: PAGE_SIZE });
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loadFailed, setLoadFailed] = useState(false);
  // W-26: the list's own failure (settings loaded), and the latest list request's number.
  const [listFailed, setListFailed] = useState(false);
  const listRequest = useRef(0);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<AddApprovedEmailsResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setSettings(await fetchJoinSettings());
    } catch {
      setLoadFailed(true);
    }
  }, []);

  const loadList = useCallback(async (q: string, p: number) => {
    const request = ++listRequest.current;
    try {
      const result = await fetchApprovedEmails({ q: q || undefined, page: p, limit: PAGE_SIZE });
      // W-26: an older search or page answering late is dropped.
      if (request !== listRequest.current) return;
      const lastPage = Math.max(1, Math.ceil(result.meta.total / PAGE_SIZE));
      if (p > lastPage) {
        // The page emptied (its last row was removed): show the new last page instead.
        setPage(lastPage);
        return;
      }
      setEntries(result.entries);
      setMeta(result.meta);
      setListFailed(false);
    } catch {
      if (request === listRequest.current) setListFailed(true);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    void loadList(search, page);
  }, [loadList, search, page]);

  async function refresh() {
    await Promise.all([loadSettings(), loadList(search, page)]);
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    setError(null);
    setNotice(null);
    setAddResult(null);
    try {
      setAddResult(await addApprovedEmails(text));
      setText("");
      await refresh();
    } catch (err) {
      setError(errorText(err, "allowlist.addFailed"));
    } finally {
      setAdding(false);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const content = await file.text();
      setText((current) => (current.trim() ? `${current.trim()}\n${content}` : content));
    } catch {
      setError(t("fileFailed"));
    }
  }

  async function handleRemove(entry: ApprovedEmail) {
    const blocking = settings?.policy === "approved_only";
    const question = blocking
      ? t("confirmRemoveBlocking", { email: entry.email })
      : t("confirmRemove", { email: entry.email });
    if (!window.confirm(question)) return;
    setError(null);
    setNotice(null);
    try {
      const result = await removeApprovedEmail(entry.id);
      setNotice(
        result.sessionsEnded > 0
          ? t("removedSignedOut", { email: result.email })
          : t("removed", { email: result.email }),
      );
    } catch (err) {
      setError(errorText(err, "allowlist.removeFailed"));
    }
    await refresh();
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(query.trim());
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-IN", { dateStyle: "medium" }).format(
      new Date(iso),
    );
  const pages = Math.max(1, Math.ceil(meta.total / PAGE_SIZE));

  if (loadFailed && !settings) {
    return (
      <p role="alert" className="text-sm text-[var(--danger)]" data-testid="approved-emails-error">
        {t("loadFailed")}
      </p>
    );
  }
  if (!settings) {
    return <Text tone="muted">{t("loading")}</Text>;
  }

  return (
    <div className="space-y-10" data-testid="approved-emails-admin">
      <JoinPolicySetting
        settings={settings}
        onChanged={(next) => {
          setSettings(next);
          void loadList(search, page);
        }}
      />

      <section className="space-y-3">
        <Heading as="h2" level="card">
          {t("addTitle")}
        </Heading>
        <form onSubmit={handleAdd} className="space-y-3" data-testid="approved-emails-add">
          <Field label={t("addLabel")} hint={t("addHint")}>
            <Textarea
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="approved-emails-text"
            />
          </Field>
          <Field label={t("uploadLabel")}>
            <Input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => void handleFile(e)}
              data-testid="approved-emails-file"
            />
          </Field>
          <Button
            type="submit"
            disabled={adding || !text.trim()}
            data-testid="approved-emails-submit"
          >
            {adding ? t("adding") : t("addSubmit")}
          </Button>
        </form>
        {addResult ? (
          <div role="status" className="space-y-1 text-sm" data-testid="approved-emails-result">
            <p>{t("addResult", { added: addResult.added, already: addResult.alreadyListed })}</p>
            {addResult.invalidCount > 0 ? (
              <>
                <p>{t("addInvalid", { count: addResult.invalidCount })}</p>
                <ul className="list-disc pl-5 text-[var(--text-muted)]">
                  {addResult.invalid.map((line, index) => (
                    <li key={`${index}-${line}`}>{line}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-2" role="search">
          <Field label={t("searchLabel")} className="min-w-[16rem] flex-1">
            <Input
              type="search"
              value={query}
              placeholder={t("searchPlaceholder")}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="approved-emails-search"
            />
          </Field>
          <Button type="submit" variant="secondary">
            {t("searchSubmit")}
          </Button>
        </form>
        {notice ? (
          <p role="status" className="text-sm" data-testid="approved-emails-notice">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="text-sm text-[var(--danger)]"
            data-testid="approved-emails-action-error"
          >
            {error}
          </p>
        ) : null}
        {listFailed ? (
          <p
            role="alert"
            className="text-sm text-[var(--danger)]"
            data-testid="approved-emails-list-error"
          >
            {t("loadFailed")}
          </p>
        ) : entries.length === 0 ? (
          <Text tone="muted" data-testid="approved-emails-empty">
            {search ? t("noMatches") : t("empty")}
          </Text>
        ) : (
          <ul className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                data-testid={`approved-email-${entry.email}`}
              >
                <div>
                  <p className="font-medium">{entry.name ?? entry.email}</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {entry.name ? `${entry.email} · ` : ""}
                    {t("addedOn", { date: formatDate(entry.createdAt) })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleRemove(entry)}
                  aria-label={t("removeLabel", { email: entry.email })}
                  data-testid={`approved-email-remove-${entry.email}`}
                >
                  {t("remove")}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {pages > 1 ? (
          <nav className="flex items-center gap-3" aria-label={t("pageOf", { page, pages })}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("previous")}
            </Button>
            <Text tone="muted">{t("pageOf", { page, pages })}</Text>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("next")}
            </Button>
          </nav>
        ) : null}
      </section>
    </div>
  );
}
